import { BaseSuggestion, Key } from '@easy-expense/data-schema-v2';
import React from 'react';

/**
 * The deleted suggestions a picker still offers: the ones transactions are still filed against.
 *
 * Deleting something takes it out of the selectable lists, but the transactions that used it keep
 * pointing at it. Those keys are exactly what a usage tally holds, so the deleted entries worth
 * offering are the tallied keys that no longer resolve to an active suggestion.
 *
 * `activeEntities` is the selectable list the caller already reads; it is only a dependency, so
 * the tally is recomputed when the store changes.
 */
export const useDeletedInUse = <E extends BaseSuggestion>(
  usageCounts: Record<string, number>,
  getByKey: (key: Key | null | undefined) => E | undefined,
  activeEntities: unknown,
): { entity: E; usageCount: number }[] => {
  return React.useMemo(() => {
    const deleted: { entity: E; usageCount: number }[] = [];

    Object.entries(usageCounts).forEach(([key, usageCount]) => {
      const entity = getByKey(key);
      if (entity && entity.entityState !== 'active') {
        deleted.push({ entity, usageCount });
      }
    });

    return deleted;
  }, [usageCounts, getByKey, activeEntities]);
};
